import { DOCUMENT, Injectable, effect, inject } from '@angular/core';

import { TenantContextService } from './tenant-context.service';

/**
 * Reactively applies the current tenant's identity to the document:
 *
 *  - `--brijjsports-tenant-accent` / `--brijjsports-tenant-sidebar-bg` CSS vars for the
 *    sidebar gradient and chrome accent.
 *  - `--brijjsports-primary` CSS var for action colors (buttons, links). Driven
 *    by the same `theme.accent` value today; kept as a separate token so
 *    chrome (accent) and action color (primary) can diverge later.
 *  - `data-tenant-type` and `data-tenant-id` attributes on `<html>` so
 *    SCSS can branch on tenant type without component overrides
 *    (see docs/brijjsports-multi-tenant-architecture.md §6).
 *  - `document.title` set to the tenant's display name.
 */
@Injectable({ providedIn: 'root' })
export class TenantThemeService {
  private readonly tenants = inject(TenantContextService);
  private readonly doc = inject(DOCUMENT);

  /** Call once during app bootstrap to start syncing tokens. */
  init(): void {
    effect(() => {
      const tenant = this.tenants.currentTenant();
      const theme = tenant.theme;
      const root = this.doc.documentElement;

      root.style.setProperty('--brijjsports-tenant-accent', theme.accent);
      root.style.setProperty('--brijjsports-tenant-sidebar-bg', theme.sidebarBg);
      root.style.setProperty('--brijjsports-primary', theme.accent);

      root.dataset['tenantType'] = tenant.kind;
      root.dataset['tenantId'] = tenant.id;

      this.doc.title = theme.displayName;
    });
  }
}
